import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, ArrowLeft, TrendingUp, Briefcase, DollarSign, Tag } from "lucide-react";
import api from "../lib/api";
import { FlowGrid } from "../components/FlowVisuals";

const CAT_LABELS = { content: "Content", design: "Design", automation: "Automation", development: "Dev", research: "Research", customer_growth: "Growth", hiring: "Hiring", operations: "Ops", finance: "Finance", branding: "Branding", web_digital: "Web", local_business: "Local", personal: "Personal", general: "General" };
const MEDAL = ["", "#FFD700", "#C0C0C0", "#CD7F32"];

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/doers/leaderboard?period=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  return (
    <div style={{ paddingTop: 84, minHeight: "100vh", position: "relative" }}>
      <FlowGrid opacity={0.02} />
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 24px 64px", position: "relative", zIndex: 1 }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", marginBottom: 24 }}>
          <ArrowLeft size={14} /> Back
        </button>

        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Trophy size={28} color="var(--primary)" style={{ marginBottom: 8 }} />
          <h1 data-testid="leaderboard-title" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "1.8rem", color: "var(--text-main)", margin: 0 }}>
            Top <span style={{ background: "linear-gradient(135deg, #005CEE, #8A4FFF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Executors</span>
          </h1>
          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem", color: "var(--text-muted)", marginTop: 6 }}>Ranked by tasks completed and total earnings</p>
        </div>

        {/* Period filter */}
        <div data-testid="period-filter" style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 28 }}>
          {[["all", "All Time"], ["month", "This Month"], ["week", "This Week"]].map(([val, label]) => (
            <button key={val} data-testid={`period-${val}`}
              onClick={() => setPeriod(val)}
              style={{
                padding: "6px 16px", borderRadius: 100, border: "none", cursor: "pointer",
                background: period === val ? "var(--primary)" : "var(--bg-soft)",
                color: period === val ? "#fff" : "var(--text-muted)",
                fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", fontWeight: 600, transition: "all 0.2s",
              }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <p style={{ textAlign: "center", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>Loading...</p>
        ) : !data?.leaderboard?.length ? (
          <div style={{ textAlign: "center", padding: "3rem 0" }}>
            <p style={{ color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem" }}>No completed tasks in this period yet.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.leaderboard.map((entry, i) => (
              <motion.div key={entry.doer_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                data-testid={`lb-entry-${entry.rank}`}
                onClick={() => navigate(`/doer/${entry.doer_id}`)}
                style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
                  borderRadius: 14, background: "var(--bg-elevated)", border: entry.rank <= 3 ? `1px solid ${MEDAL[entry.rank]}30` : "1px solid var(--bg-neutral)",
                  boxShadow: entry.rank === 1 ? "0 4px 20px rgba(255,215,0,0.08)" : "0 2px 12px rgba(0,0,0,0.02)",
                  cursor: "pointer", transition: "transform 0.15s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}>
                {/* Rank */}
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  background: entry.rank <= 3 ? `linear-gradient(135deg, ${MEDAL[entry.rank]}, ${MEDAL[entry.rank]}88)` : "var(--bg-soft)",
                }}>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "0.75rem", color: entry.rank <= 3 ? "#fff" : "var(--text-muted)" }}>
                    {entry.rank}
                  </span>
                </div>

                {/* Avatar + Name */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.88rem", color: "var(--text-main)" }}>{entry.name}</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                    {entry.specialties.map(s => (
                      <span key={s} style={{ padding: "1px 6px", borderRadius: 100, background: "var(--bg-soft)", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.48rem", fontWeight: 600, color: "var(--text-muted)" }}>
                        {CAT_LABELS[s] || s}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: "0.78rem", color: "var(--text-main)", display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                      <Briefcase size={11} color="var(--primary)" />{entry.tasks_completed}
                    </div>
                    <div style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.52rem", color: "var(--text-muted)" }}>tasks</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: "0.78rem", color: "var(--primary)", display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                      <DollarSign size={11} />{Math.round(entry.total_earned)}c
                    </div>
                    <div style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.52rem", color: "var(--text-muted)" }}>earned</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
